import QuillDelta from "quill-delta";
import { LineCap } from "../standardShapes/LineCap";

import { ShapeType } from "./ShapeType";

export enum AttributeKey {
  Alpha = "alpha",
  AspectRatio = "aspectRatio",
  BoundingBoxOriginalSize = "boundingBoxOriginalSize",
  BoundingBoxSize = "boundingBoxSize",
  BoundingBoxOrigin = "boundingBoxOrigin",
  EraserBlocklist = "eraserBlocklist",
  FillColor = "fillColor",
  ImageURL = "imageURL",
  IsOpen = "isOpen",
  LineCapA = "lineCapA",
  LineCapB = "lineCapB",
  MaxWidth = "maxWidth",
  PageNumber = "pageNumber",
  PointA = "pointA",
  PointB = "pointB",
  Points = "points",
  ShapeType = "shapeType",
  StrokeColor = "strokeColor",
  StrokeWidth = "strokeWidth",
  StrokeWidthScalesToScreen = "strokeWidthScalesToScreen",
  Text = "text",
  Translate = "translate",
  ZIndex = "zIndex",
}
export const AttributeKeyStrings: Record<string, AttributeKey> = {};
for (const k in AttributeKey) {
  AttributeKeyStrings[AttributeKey[k as keyof typeof AttributeKey]] =
    k as AttributeKey;
}

export interface ShapeTypeAttribute {
  key: AttributeKey.ShapeType;
  value: ShapeType;
}
export interface HasShapeType {
  shapeType: ShapeType;
}

export interface AlphaAttribute {
  key: AttributeKey.Alpha;
  value: number;
}
export interface HasAlpha {
  alpha: number;
}

export interface AspectRatioAttribute {
  key: AttributeKey.AspectRatio;
  value?: number; // (width / height)
}

export interface HasAspectRatio {
  aspectRatio?: number;
}

export interface BoundingBoxOriginalSizeAttribute {
  key: AttributeKey.BoundingBoxOriginalSize;
  value: [number, number];
}
export interface HasBoundingBoxOriginalSize {
  boundingBoxOriginalSize: [number, number];
}

export interface BoundingBoxSizeAttribute {
  key: AttributeKey.BoundingBoxSize;
  value: [number, number];
}
export interface HasBoundingBoxSize {
  boundingBoxSize: [number, number];
}

export interface BoundingBoxOriginAttribute {
  key: AttributeKey.BoundingBoxOrigin;
  value: [number, number];
}
export interface HasBoundingBoxOrigin {
  boundingBoxOrigin: [number, number];
}

export interface EraserBlocklistAttribute {
  key: AttributeKey.EraserBlocklist;
  value: string[];
}
export interface HasEraserBlocklist {
  /// Optional!
  eraserBlocklist?: string[];
}

export interface FillColorAttribute {
  key: AttributeKey.FillColor;
  value: string;
}
export interface HasFillColor {
  fillColor: string;
}

export interface ImageURLAttribute {
  key: AttributeKey.ImageURL;
  value?: string;
}

export interface HasImageURL {
  imageURL?: string;
}

export interface IsOpenAttribute {
  key: AttributeKey.IsOpen;
  value: boolean;
}
export interface HasIsOpen {
  isOpen: boolean;
}

export interface LineCapAAttribute {
  key: AttributeKey.LineCapA;
  value: LineCap;
}
export interface HasLineCapA {
  lineCapA: LineCap;
}

export interface LineCapBAttribute {
  key: AttributeKey.LineCapB;
  value: LineCap;
}
export interface HasLineCapB {
  lineCapB: LineCap;
}

export interface MaxWidthAttribute {
  key: AttributeKey.MaxWidth;
  value: number | null;
}
export interface HasMaxWidth {
  maxWidth: number | null;
}

export interface PageNumberAttribute {
  key: AttributeKey.PageNumber;
  value: number;
}

export interface HasPageNumber {
  pageNumber?: number;
}

export interface PointAAttribute {
  key: AttributeKey.PointA;
  value: [number, number];
}
export interface HasPointA {
  pointA: [number, number];
}

export interface PointBAttribute {
  key: AttributeKey.PointB;
  value: [number, number];
}
export interface HasPointB {
  pointB: [number, number];
}

export interface PointsAttribute {
  key: AttributeKey.Points;
  value: [number, number][];
}
export interface HasPoints {
  points: [number, number][];
}

export interface StrokeColorAttribute {
  key: AttributeKey.StrokeColor;
  value: string;
}
export interface HasStrokeColor {
  strokeColor: string;
}

export interface StrokeWidthAttribute {
  key: AttributeKey.StrokeWidth;
  value: number;
}
export interface HasStrokeWidth {
  strokeWidth: number;
}

export interface StrokeWidthScalesToScreenAttribute {
  key: AttributeKey.StrokeWidthScalesToScreen;
  value?: boolean;
}
export interface HasStrokeWidthScalesToScreen {
  strokeWidthScalesToScreen?: boolean;
}

export interface TextAttribute {
  key: AttributeKey.Text;
  value: QuillDelta | null;
}
export interface HasText {
  text: QuillDelta | null;
}

export interface TranslateAttribute {
  key: AttributeKey.Translate;
  value: [number, number];
}
export interface HasTranslate {
  translate: [number, number];
}

export interface ZIndexAttribute {
  key: AttributeKey.ZIndex;
  value: number;
}
export interface HasZIndex {
  zIndex: number;
}

// discriminated union
export type SubjectAttribute = {
  key: AttributeKey;
  value: unknown;
};

type HasEverything = HasAlpha &
  HasAspectRatio &
  HasBoundingBoxOriginalSize &
  HasBoundingBoxSize &
  HasBoundingBoxOrigin &
  HasEraserBlocklist &
  HasFillColor &
  HasImageURL &
  HasIsOpen &
  HasLineCapA &
  HasLineCapB &
  HasMaxWidth &
  HasPageNumber &
  HasPointA &
  HasPointB &
  HasPoints &
  HasShapeType &
  HasStrokeColor &
  HasStrokeWidth &
  HasStrokeWidthScalesToScreen &
  HasText &
  HasTranslate &
  HasZIndex;

export type PartialUnknownShape = Partial<HasEverything>;
